export const GA_MEASUREMENT_ID = "G-7BNXHQ06BB";

const GA_SCRIPT_ID = "idkf-google-analytics";
const GA_SCRIPT_SRC = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
const ANALYTICS_BOOT_DELAY_MS = 8000;
const INTENT_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"];

let loadScheduled = false;
let loadStarted = false;
let gtagConfigured = false;
let loadTimerId = null;
let idleCallbackId = null;
let cleanupIntentListeners = null;
let pageViewQueue = [];
let lastPagePath = null;

function hasBrowser() {
  return (
    typeof window !== "undefined" &&
    typeof document !== "undefined" &&
    Boolean(document.head)
  );
}

function gtag() {
  if (!hasBrowser()) return null;

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function pushToDataLayer() {
      window.dataLayer.push(arguments);
    };

  return window.gtag;
}

function clearScheduledLoad() {
  if (!hasBrowser()) return;

  if (loadTimerId !== null) {
    window.clearTimeout(loadTimerId);
    loadTimerId = null;
  }

  if (idleCallbackId !== null) {
    window.cancelIdleCallback?.(idleCallbackId);
    idleCallbackId = null;
  }

  if (cleanupIntentListeners) {
    cleanupIntentListeners();
    cleanupIntentListeners = null;
  }
}

function configureGtag() {
  const send = gtag();
  if (!send || gtagConfigured) return send;

  send("js", new Date());
  send("config", GA_MEASUREMENT_ID, { send_page_view: false });
  gtagConfigured = true;

  return send;
}

function flushPageViews() {
  const send = configureGtag();
  if (!send) return;

  const queued = pageViewQueue;
  pageViewQueue = [];

  queued.forEach(({ pagePath, pageTitle }) => {
    send("event", "page_view", {
      page_path: pagePath,
      page_title: pageTitle,
      send_to: GA_MEASUREMENT_ID,
    });
  });
}

function appendAnalyticsScript() {
  if (!hasBrowser()) return;

  const existing = document.getElementById(GA_SCRIPT_ID);
  if (existing) {
    flushPageViews();
    return;
  }

  const script = document.createElement("script");
  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src = GA_SCRIPT_SRC;
  script.onerror = () => {};
  document.head.appendChild(script);

  flushPageViews();
}

function loadGoogleAnalytics() {
  if (!hasBrowser() || loadStarted) return;

  loadStarted = true;
  clearScheduledLoad();
  appendAnalyticsScript();
}

function runWhenIdle(callback) {
  if (typeof window.requestIdleCallback === "function") {
    idleCallbackId = window.requestIdleCallback(callback, { timeout: 2000 });
    return;
  }

  idleCallbackId = window.setTimeout(callback, 0);
}

function addIntentListeners() {
  const onIntent = () => {
    loadGoogleAnalytics();
  };

  INTENT_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, onIntent, {
      once: true,
      passive: true,
    });
  });

  cleanupIntentListeners = () => {
    INTENT_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, onIntent);
    });
  };
}

function scheduleAnalyticsLoad() {
  if (!hasBrowser() || loadScheduled || loadStarted) return;

  loadScheduled = true;
  addIntentListeners();

  loadTimerId = window.setTimeout(() => {
    loadTimerId = null;
    runWhenIdle(loadGoogleAnalytics);
  }, ANALYTICS_BOOT_DELAY_MS);
}

function currentPagePath() {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

export function trackPageView(pagePath = currentPagePath(), pageTitle = document.title) {
  if (!hasBrowser()) return;
  if (!pagePath || pagePath === lastPagePath) return;

  lastPagePath = pagePath;
  pageViewQueue.push({ pagePath, pageTitle });

  if (loadStarted) {
    flushPageViews();
    return;
  }

  scheduleAnalyticsLoad();
}

export function resetAnalyticsForTests() {
  clearScheduledLoad();

  const script = hasBrowser() ? document.getElementById(GA_SCRIPT_ID) : null;
  script?.remove();

  loadScheduled = false;
  loadStarted = false;
  gtagConfigured = false;
  pageViewQueue = [];
  lastPagePath = null;

  if (hasBrowser()) {
    delete window.dataLayer;
    delete window.gtag;
  }
}
